import { BuildPresentationProps } from '@features/lci-report/subdomains/visitation/utils/build-presentation';
import { visitAttributionDimensionsSlide } from './visit-attribution-dimensions-slide';
import { lciOptimizationIndexSlide } from './lci-optimization-index-slide';
import { DIMENSIONS_PER_SLIDE } from '../../../../slides/slide-layout';
import { Slide } from '@features/powerpoint';
import { footTrafficTitleSlide } from './visit-attribution-title-slide';
import { overallCampaignResultsSlide } from './overall-campaign-results';
import { lciLiteSummarySlide } from './lci-lite-summary-slide';
import {
  getTopAndBottomPerformers,
  Performers,
} from '@features/reports/subdomains/visitation';

const VISIT_ATTRIBUTION_TOP_PERFORMER_SLIDE_HEADER =
  'Top Performing Dimensions (Visit Attribution)';
const VISIT_ATTRIBUTION_BOTTOM_PERFORMER_SLIDE_HEADER =
  'Bottom Performing Dimensions (Visit Attribution)';
const PERFORMER_COUNT = 3;

const visitAttributionTitleSlideFromProps = (
  props: BuildPresentationProps
): Slide[] =>
  props.customFields.includedSlides.footTraffic.titleSlide
    ? [footTrafficTitleSlide]
    : [];

const overallCampaignResultsSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.footTraffic.overallResults
    ? [
        props.customFields.includedSlidesPreset === 'lciBrief'
          ? lciLiteSummarySlide({
              slideMaster,
              totalVisits: props.visitMetrics.projectedVisits ?? 0,
              costPerVisit: props.calculatedFields.costPerVisit ?? 0,
              measurementStartDate: props.campaign.startDate,
              measurementEndDate: props.selectedReportDate,
              locationsMeasured: props.visitMetrics.locationsMeasured,
              keyTakeaways: props.customFields.ftaOverallResults.split('\n'),
            })
          : overallCampaignResultsSlide({
              slideMaster,
              totalVisits: props.visitMetrics.projectedVisits ?? 0,
              incrementalVisits: props.visitMetrics.incrementalVisits ?? 0,
              incrementalLift: props.visitMetrics.incrementalLift ?? 0,
              costPerVisit: props.calculatedFields.costPerVisit ?? 0,
              costPerIncrementalVisit:
                props.calculatedFields.costPerIncrementalVisit ?? 0,
              confidenceLevel: props.visitMetrics.confidenceLevel ?? 0,
              ftaOverallResults: props.customFields.ftaOverallResults,
            }),
      ]
    : [];
};

const lciOptimizationIndexSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  if (!props.customFields.includedSlides.footTraffic.optimizationIndex)
    return [];

  const outerPerformers = getTopAndBottomPerformers(
    props.subdimensions,
    'optimizationIndex',
    PERFORMER_COUNT
  );

  const orderedKeys = (
    Object.keys(outerPerformers).toSorted((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    ) as (keyof typeof outerPerformers)[]
  ).reduce(
    (acc, key) => {
      acc[key] = outerPerformers[key];
      return acc;
    },
    {} as typeof outerPerformers
  );

  const groupedPerformers = Object.entries(orderedKeys).reduce(
    (
      accumulator: Record<string, Performers>[],
      [categoryTitle, performers],
      index
    ) => {
      const chunkIndex = Math.floor(index / DIMENSIONS_PER_SLIDE);

      accumulator[chunkIndex] ??= {};

      accumulator[chunkIndex][categoryTitle] = performers;

      return accumulator;
    },
    []
  );

  const slides = groupedPerformers.map(topBottomPerformers =>
    lciOptimizationIndexSlide({
      slideMaster,
      topBottomPerformers,
    })
  );

  return slides;
};

const topFtaDimensionSlidesFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  const slideHeader =
    props.customFields.dimensions.Top.visitsTableHeader.length > 0
      ? props.customFields.dimensions.Top.visitsTableHeader
      : VISIT_ATTRIBUTION_TOP_PERFORMER_SLIDE_HEADER;

  return (props.dimensionTableData.Top ?? []).length > 0
    ? [
        visitAttributionDimensionsSlide({
          slideMaster,
          subdimensions: props.dimensionTableData.Top ?? [],
          overallVisitMetrics: props.visitMetrics,
          slideHeader,
          calculatedFields: props.calculatedFields,
        }),
      ]
    : [];
};

const bottomFtaDimensionSlidesFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  const slideHeader =
    props.customFields.dimensions.Bottom.visitsTableHeader.length > 0
      ? props.customFields.dimensions.Bottom.visitsTableHeader
      : VISIT_ATTRIBUTION_BOTTOM_PERFORMER_SLIDE_HEADER;

  return (props.dimensionTableData.Bottom ?? []).length > 0
    ? [
        visitAttributionDimensionsSlide({
          slideMaster,
          subdimensions: props.dimensionTableData.Bottom ?? [],
          overallVisitMetrics: props.visitMetrics,
          slideHeader,
          calculatedFields: props.calculatedFields,
        }),
      ]
    : [];
};

const ftaDimensionSlidesFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  if (!props.customFields.includedSlides.footTraffic.dimensions) return [];

  const topDimensionSlides = topFtaDimensionSlidesFromProps(props, slideMaster);
  const bottomDimensionSlides = bottomFtaDimensionSlidesFromProps(
    props,
    slideMaster
  );

  return [...topDimensionSlides, ...bottomDimensionSlides];
};

export const visitAttributionSlides = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  const slides = [
    visitAttributionTitleSlideFromProps(props),
    overallCampaignResultsSlideFromProps(props, slideMaster),
    lciOptimizationIndexSlideFromProps(props, slideMaster),
    ftaDimensionSlidesFromProps(props, slideMaster),
  ].flat();

  return slides;
};
